import * as React from "react";
import { RichTreeView, TreeItem2Props, TreeItem2, TreeItem2IconContainer } from "@mui/x-tree-view";
import caretRightSvg from "./images/caret-circle-right.svg?react";
import caretDownSvg from "./images/caret-circle-down.svg?react";
import NoResultImage from "../table/images/table-no-results-available.svg";
import styles from "./tree.module.css";

type CoreTreeViewProps<D extends object> = {
	disableSelection?: boolean;
	items: D[];
	expandIcon?: React.FunctionComponent<
		React.SVGProps<SVGSVGElement> & {
			title?: string;
		}
	>;
	collapseIcon?: React.FunctionComponent<
		React.SVGProps<SVGSVGElement> & {
			title?: string;
		}
	>;
	label: React.ElementType;
	getItemId?: (item: D) => string;
	getItemLabel?: (item: D) => string;
	noResultString?: string;
	noResultIcon?: string;
};

const CustomIconContainer = (props: { children: string }) => {
	return (
		<TreeItem2IconContainer
			sx={{
				width: "20px",
			}}
		>
			{props.children}
		</TreeItem2IconContainer>
	);
};

export function CoreTreeView<D extends object>(props: CoreTreeViewProps<D>) {
	const CustomTreeItem = React.forwardRef<HTMLLIElement, TreeItem2Props & { labelComponent: React.ElementType }>(
		function CustomTreeItemComponent(props, ref) {
			const { labelComponent: LabelComponent, ...restProps } = props;
			return (
				<TreeItem2
					ref={ref}
					style={{
						fontSize: "14px",
					}}
					{...restProps}
					slots={{
						label: LabelComponent,
						iconContainer: CustomIconContainer,
					}}
					slotProps={{
						label: {
							id: `${props.itemId}`,
						},
					}}
				/>
			);
		},
	);
	return (
		<>
			{props.items.length > 0 && (
				<RichTreeView
					items={props.items}
					disableSelection={props.disableSelection}
					slots={{
						expandIcon: props.expandIcon ?? caretRightSvg,
						collapseIcon: props.collapseIcon ?? caretDownSvg,
						item: (itemProps: TreeItem2Props) => <CustomTreeItem {...itemProps} labelComponent={props.label} />,
					}}
					getItemId={props.getItemId}
					getItemLabel={props.getItemLabel}
					sx={{
						"& .MuiTreeItem-content": {
							borderRadius: "50px",
							"&.Mui-selected": {
								backgroundColor: "#d5effd",
							},
							"&.Mui-focused": {
								backgroundColor: "transparent",
								outline: "2px solid var(--focus-outline-color-dark)",
								borderRadius: "2px",
							},
							"&:hover": {
								backgroundColor: "#f7f7f7",
							},
						},
						"& .MuiCollapse-root": {
							"--TreeView-itemChildrenIndentation": "25px", // Increase left padding for children
						},
					}}
				/>
			)}
			{props.items.length === 0 && (
				<div className={styles["no-results"]}>
					<img
						aria-label="No Search Result Image"
						src={props.noResultIcon ?? NoResultImage}
						className={styles["no-results-img"]}
					/>
					<p className={styles["no-results-p"]}>{props.noResultString}</p>
				</div>
			)}
		</>
	);
}
